import styled from '@emotion/styled';
import type { ActivityUsage, Activity } from '@kizen/api/activities';
import {
  InformationTree,
  type InformationTreeProps,
} from '@kizen/kds/InformationTree';
import { Typography } from '@kizen/kds/Typography';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { hideScrollbarCss } from 'app/spacing';
import Loader from '__components/Kizen/Loader';
import BasicModal from '__components/Modals/presets/BasicModal';
import { toastVariant, useToast } from '__components/ToastProvider';
import { getErrorMessage } from '__hooks/useErrors';
import { getActivityUsageQuery } from 'query/activities';
import { getOriginalError } from 'services/AxiosService';
import { getBusinessClientObject } from 'store/authentication/selectors';
import {
  agenticWorkflowHref,
  dashboardHref,
  filterGroupHref,
  homepageHref,
  objectHref,
  smartConnectorHref,
  toolbarTemplateHref,
} from 'utility/assetHrefs';
import { UsageScrollContainer } from './UsageScrollContainer';

type TextData = Extract<
  InformationTreeProps['sections'][number]['items'][number],
  { type: 'text' }
>;

type UsageSection =
  | Exclude<keyof ActivityUsage, 'scheduled_activities' | 'logged_activities'>
  | 'activities';

type GetUsageTextData = (params: {
  data: ActivityUsage;
  t: TFunction;
}) => TextData[];

type ActivityUsageProps = {
  activity: Activity;
  onHide(): void;
};

const defaultUsage: ActivityUsage = {
  scheduled_activities: 0,
  logged_activities: 0,
  automations: [],
  smart_connectors: [],
  dashboards: [],
  homepages: [],
  filter_groups: [],
  toolbar_templates: [],
};

const useUsageTextData = (): Record<UsageSection, GetUsageTextData> => {
  const clientObject = useSelector(getBusinessClientObject);

  const activities: GetUsageTextData = ({ data, t }) => {
    const { scheduled_activities, logged_activities } = data;

    if (!scheduled_activities && !logged_activities) {
      return [{ type: 'text', label: t('None') }];
    }

    const scheduled =
      scheduled_activities === 1
        ? t('{{ count }} scheduled activity', { count: scheduled_activities })
        : t('{{ count }} scheduled activities', {
            count: scheduled_activities,
          });

    const logged =
      logged_activities === 1
        ? t('{{ count }} logged activity', { count: logged_activities })
        : t('{{ count }} logged activities', { count: logged_activities });

    return [{ type: 'text', label: `${scheduled}, ${logged}` }];
  };

  const automations: GetUsageTextData = ({ data, t }) => {
    if (!data.automations.length) {
      return [{ type: 'text', label: t('None') }];
    }

    const items = data.automations.map((auto) => {
      const trailing: string[] = [];

      if (auto.triggers.length === 1) {
        trailing.push(t('{{ count }} trigger', { count: 1 }));
      } else if (auto.triggers.length > 1) {
        trailing.push(
          t('{{ count }} triggers', { count: auto.triggers.length })
        );
      }

      if (auto.variables.length === 1) {
        trailing.push(t('{{ count }} variable', { count: 1 }));
      } else if (auto.variables.length > 1) {
        trailing.push(
          t('{{ count }} variables', { count: auto.variables.length })
        );
      }

      if (auto.steps.length === 1) {
        trailing.push(t('{{ count }} action', { count: 1 }));
      } else if (auto.steps.length > 1) {
        trailing.push(t('{{ count }} actions', { count: auto.steps.length }));
      }

      if (auto.conditions.length === 1) {
        trailing.push(t('{{ count }} condition', { count: 1 }));
      } else if (auto.conditions.length) {
        trailing.push(
          t('{{ count }} conditions', { count: auto.conditions.length })
        );
      }

      if (auto.goals.length === 1) {
        trailing.push(t('{{ count }} goal', { count: 1 }));
      } else if (auto.goals.length > 1) {
        trailing.push(t('{{ count }} goals', { count: auto.goals.length }));
      }

      const text: TextData = {
        type: 'text',
        label: auto.display_name,
        trailingText: [
          { type: 'text', text: t('in') },
          { type: 'space' },
          { type: 'text', text: trailing.join(', ') },
        ],
      };

      if (auto.can_view) {
        text.href = agenticWorkflowHref(auto.id);
      }

      return text;
    });

    return items;
  };

  const dashboards: GetUsageTextData = ({ data, t }) => {
    if (!data.dashboards.length) {
      return [{ type: 'text', label: t('None') }];
    }

    const items = data.dashboards.map((dashboard) => {
      const text: TextData = { type: 'text', label: dashboard.display_name };

      if (dashboard.can_view) {
        text.href = dashboardHref(dashboard.id);
      }

      return text;
    });

    return items;
  };

  const filter_groups: GetUsageTextData = ({ data, t }) => {
    if (!data.filter_groups.length) {
      return [{ type: 'text', label: t('None') }];
    }

    const items = data.filter_groups.map((fg) => {
      const base = objectHref(fg.custom_object_id, clientObject.id);

      const item: TextData = {
        type: 'text',
        label: fg.display_name,
        trailingText: [
          { type: 'text', text: t('on') },
          { type: 'space' },
          fg.can_view
            ? { type: 'link', text: fg.custom_object_name, href: base }
            : { type: 'text', text: fg.custom_object_name },
        ],
      };

      if (fg.can_view) {
        item.href = filterGroupHref(
          fg.id,
          fg.custom_object_id,
          clientObject.id
        );
      }

      return item;
    });

    return items;
  };

  const homepages: GetUsageTextData = ({ data, t }) => {
    if (!data.homepages.length) {
      return [{ type: 'text', label: t('None') }];
    }

    const items = data.homepages.map((homepage) => {
      const text: TextData = {
        type: 'text',
        label: homepage.display_name,
      };

      if (homepage.can_view) {
        text.href = homepageHref(homepage.id);
      }

      return text;
    });

    return items;
  };

  const smart_connectors: GetUsageTextData = ({ data, t }) => {
    if (!data.smart_connectors.length) {
      return [{ type: 'text', label: t('None') }];
    }

    const items = data.smart_connectors.map((sc) => {
      const text: TextData = {
        type: 'text',
        label: sc.display_name,
      };

      if (sc.can_view) {
        text.href = smartConnectorHref(sc.id);
      }

      return text;
    });

    return items;
  };

  const toolbar_templates: GetUsageTextData = ({ data, t }) => {
    if (!data.toolbar_templates.length) {
      return [{ type: 'text', label: t('None') }];
    }

    const items = data.toolbar_templates.map((tt) => {
      const text: TextData = {
        type: 'text',
        label: tt.display_name,
      };

      if (tt.can_view) {
        text.href = toolbarTemplateHref(tt.id);
      }

      return text;
    });

    return items;
  };

  return {
    activities,
    automations,
    dashboards,
    filter_groups,
    homepages,
    smart_connectors,
    toolbar_templates,
  };
};

const StyledModal = styled(BasicModal)`
  .modal-body {
    ${hideScrollbarCss}
    display: flex;
    flex-direction: column;
    gap: 25px;
    padding-right: 0;
  }
`;

export const ActivityUsageModal = (props: ActivityUsageProps) => {
  const { activity, onHide } = props;
  const { t } = useTranslation();
  const [showToast] = useToast();
  const usageText = useUsageTextData();

  const {
    data = defaultUsage,
    isLoading,
    error,
  } = useQuery(
    getActivityUsageQuery(activity.id, {
      staleTime: 0,
      gcTime: 0,
      throwOnError: false,
    })
  );

  const sections = useMemo(() => {
    const sections: InformationTreeProps['sections'] = [
      {
        label: t('Activities'),
        icon: 'asset-activities',
        items: usageText.activities({ data, t }),
      },
      {
        label: t('Agentic Workflows'),
        icon: 'asset-automations',
        items: usageText.automations({ data, t }),
      },
      {
        label: t('SmartConnectors'),
        icon: 'asset-smart-connectors',
        items: usageText.smart_connectors({ data, t }),
      },
      {
        label: t('Dashboards'),
        icon: 'asset-dashboards',
        items: usageText.dashboards({ data, t }),
      },
      {
        label: t('Homepages'),
        icon: 'asset-homepages',
        items: usageText.homepages({ data, t }),
      },
      {
        label: t('Filter Groups'),
        icon: 'asset-filter-groups',
        items: usageText.filter_groups({ data, t }),
      },
      {
        label: t('Toolbar Templates'),
        icon: 'asset-toolbar-templates',
        items: usageText.toolbar_templates({ data, t }),
      },
    ];

    return sections;
  }, [t, data, usageText]);

  useEffect(() => {
    if (error) {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('Failed to load usage data.'),
        variant: toastVariant.FAILURE,
      });
    }
  }, [error, showToast, t]);

  return (
    <StyledModal
      show
      size="medium"
      buttonText={t('Close')}
      actionBtnColor="blue"
      heading={t('{{ name }}: Where Used', { name: activity.name })}
      leftBtn={null}
      onConfirm={onHide}
      onHide={onHide}
      enforceFocus={false}
    >
      <Loader loading={isLoading} paddingTop={0}>
        <Typography color="font-primary">
          {t('This Activity is currently being used in:')}
        </Typography>
        <UsageScrollContainer>
          <InformationTree sections={sections} />
        </UsageScrollContainer>
      </Loader>
    </StyledModal>
  );
};
